import "#legacy/utilities/appointment_date.js";
import "#legacy/utilities/request_locale.js";

// Merge into the existing namespace rather than replacing it, because
// sibling modules (e.g. slot_suggester/models/*.js) may have already done
// `window.AppointmentBooking ??= {}` to install their own sub-namespaces.
// A naïve `if (!window.AppointmentBooking)` guard would then skip installing
// the public API methods (`showWizard`, `showDoctorFinder`, …) entirely.
window.AppointmentBooking ??= {};
Object.assign(window.AppointmentBooking, {
  showDoctorFinder(container, options = {}) {
    return jQuery.when(this.loadConfig(), this.loadDoctorFinder()).done(() => {
      const doctorFinder = new DoctorFinder(container, options);
      jQuery(container).on(DoctorFinder.Events.wizard.slotPick, (_event, _slot, slotElement) =>
        AppointmentBooking.decorateSlotUrl(slotElement, "doctor_finder"),
      );
      // Expose the instance on the container element for legacy readers; the `<sp-finder>` wrapper
      // forwards its actions to the instance.
      const containerElement = jQuery(container)[0];
      if (containerElement) {
        // If the provider gate has flagged that fetching should be blocked, pause
        // immediately so the initial route handler's fetch is discarded.
        if (containerElement.closest("sp-finder")?.fetchingHeld) {
          doctorFinder.results.pauseFetching();
          doctorFinder.results.reset();
        }
        containerElement.doctorFinder = doctorFinder;
        containerElement.dispatchEvent(new CustomEvent("doctorFinder:ready", { detail: doctorFinder, bubbles: false }));
      }
      return doctorFinder;
    });
  },

  loadDoctorFinder() {
    // Doctor finder is now statically bundled by the Vite legacy entrypoint
    // (vite-plugin-legacy-per-domain), so the codeload manifest below is a
    // no-op. window.DoctorFinder is already defined; just resolve immediately.
    if (window.DoctorFinder && typeof window.DoctorFinder === "function") {
      return jQuery.Deferred().resolve().promise();
    }
    const manifest = {};
    manifest[window.SparkleConfig.assetPaths.momentWithLocales] = !window.moment;
    manifest[window.SparkleConfig.assetPaths.sparkleBackbone] = !window.Backbone;
    manifest[window.SparkleConfig.assetPaths.physicianFinder] = true;
    for (var url of Array.from(window.SparkleConfig.assetPaths.viteCustomElements)) {
      manifest[url] = true;
    }
    return Sparkle.Codeload.importManifest(manifest);
  },

  createProviderWidget(providerType, providerId, options = {}) {
    const deferred = new jQuery.Deferred();
    jQuery.when(this.loadConfig()).done(() => {
      const model = new Backbone.Model({ providerType, providerId });
      const widgetView = new AppointmentBooking.ProviderWidget.Views.Widget({
        ...options,
        model,
      });

      jQuery(widgetView.el).on(AppointmentBooking.ProviderWidget.Events.slotPick, (_event, _slot, slotElement) =>
        AppointmentBooking.decorateSlotUrl(slotElement, "provider_widget"),
      );

      return Backbone.listenToOnce(widgetView, "ready", () => deferred.resolve(widgetView));
    });
    return deferred.promise();
  },

  createRelatedProvidersWidget(providerType, providerId, options) {
    const deferred = new jQuery.Deferred();
    jQuery.when(this.loadConfig()).done(() => {
      // TODO: Fix hardcoding the passiveBoost param ensure the correct scope_cache is used.
      // PassiveBoost is used to determine the scope_cache but is not passed in via the url params.
      // We debated about passing in the scope_cache_key but the params would still need to be set
      // as a fallback in case the cache was stale
      const model = new Backbone.Model(
        jQuery.extend(
          {
            searchUrl: null,
            sort: "best_match",
            openScheduling: true,
            passiveBoosts: true,
            limitBefore: 1,
            limitAfter: 2,
          },
          options,
          { currentProviderType: providerType, currentProviderId: providerId },
        ),
      );
      const widgetView = new AppointmentBooking.RelatedProvidersWidget.Views.Widget({ model });
      return Backbone.listenToOnce(widgetView, "ready", () => deferred.resolve(widgetView));
    });
    return deferred.promise();
  },

  showWizard(slotId, options) {
    const deferred = new jQuery.Deferred();
    jQuery.when(this.loadConfig(), this.loadWizard()).done(() => {
      jQuery.extend(AppointmentBooking.config.i18n.wizard, {
        help_phone_number: AppointmentBooking.config.help_phone_number,
      });
      const wizard = new AppointmentBooking.Wizard(slotId, options);
      return deferred.resolveWith(null, [wizard]);
    });

    return deferred.promise();
  },

  // Dynamically load wizard js and css for performance
  loadWizard() {
    let url;
    const manifest = {};
    manifest[window.SparkleConfig.assetPaths.momentWithLocales] = !window.moment;
    manifest[window.SparkleConfig.assetPaths.sparkleBackbone] = !window.Backbone;
    for (url of Array.from(window.SparkleConfig.assetPaths.viteCustomElements)) {
      manifest[url] = true;
    }
    manifest[window.SparkleConfig.assetPaths.appointmentBookingWizard] = true;
    for (url of Array.from(window.SparkleConfig.assetPaths.viteDecisionTreeViewer)) {
      manifest[url] = true;
    }
    return Sparkle.Codeload.importManifest(manifest);
  },

  showSlotPickerModal({ focusDate, filters, options }) {
    const deferred = new jQuery.Deferred();
    jQuery.when(this.loadConfig(), this.loadSlotPickerModal()).done(() => {
      const slotPickerModal = new AppointmentBooking.SlotPickerModal({ focusDate, filters, options });
      deferred.resolveWith(null, [slotPickerModal]);
      return slotPickerModal.showListView();
    });

    return deferred.promise();
  },

  // Dynamically load slot_picker_modal js and css for performance
  loadSlotPickerModal() {
    const manifest = {};
    manifest[window.SparkleConfig.assetPaths.momentWithLocales] = !window.moment;
    manifest[window.SparkleConfig.assetPaths.sparkleBackbone] = !window.Backbone;
    for (var url of Array.from(window.SparkleConfig.assetPaths.viteCustomElements)) {
      manifest[url] = !customElements.get("sp-modal");
    }
    manifest[window.SparkleConfig.assetPaths.appointmentBookingSlotPickerModal] = true;
    return Sparkle.Codeload.importManifest(manifest);
  },

  // Decorate a slot element's href with referring analytics params so the Appointment Booking Wizard can record how the
  // user reached the booking page
  decorateSlotUrl: (slotElement, referring_ui_component) => {
    if (!window.URLSearchParams) {
      return;
    }

    slotElement = jQuery(slotElement);
    const url = new URL(slotElement.attr("href"));
    url.searchParams.set("referring_url", window.location.toString());
    url.searchParams.set("referring_ui_component", referring_ui_component);

    return jQuery(slotElement).attr("href", url.toString());
  },

  initializeSlotSuggester(options) {
    const deferred = new jQuery.Deferred();
    jQuery.when(this.loadConfig()).done(() => {
      jQuery.extend(AppointmentBooking.config.i18n.slot_suggester, {
        help_phone_number: AppointmentBooking.config.help_phone_number,
      });
      const slotSelector = new AppointmentBooking.SlotSuggester(options);
      return deferred.resolveWith(null, [slotSelector]);
    });

    return deferred.promise();
  },

  loadConfig() {
    let url = window.Sparkle.apiHost
      ? window.Sparkle.apiHost + window.SparkleConfig.routes.embed.configurationEhrAppointments
      : undefined;

    // Forward the active locale so the config — which carries the widget's i18n
    // strings — is served in that language. Unknown values and the default
    // locale are dropped, so those requests keep the cacheable locale-free URL.
    const locale = Sparkle.Utilities.requestLocale();
    if (url && locale) {
      url += (url.indexOf("?") >= 0 ? "&" : "?") + "locale=" + encodeURIComponent(locale);
    }

    return (
      this.loadConfigPromise ||
      (this.loadConfigPromise = jQuery.getJSON(url).then((response) => {
        // Ensure the returned promise waits for this assignment
        window.AppointmentBooking.config = response;
        return response;
      }))
    );
  },
});
